import React, { useMemo } from 'react';

import { routes } from '@aline/routes';
import { Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useOnboarding } from 'src/components/get-started/onboarding';
import { makeStyles } from 'tss-react/mui';

import { useLeftNavItemStyles } from './LeftNavItem';
import { NavItemText } from './NavItemText';

const useStyles = makeStyles()(theme => ({
  circleNumber: {
    color: 'white',
    fontSize: 14,
    fontWeight: 600,
  },
  navItems: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 1,
    gap: theme.spacing(0.25),
    justifyContent: 'center',
    minWidth: 0,
    width: '100%',
  },
  progressContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
  },
  progressNumber: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
}));

interface ProgressCircleProps {
  progress: number; // 0 to 1
  remainingCount: number;
}

function ProgressCircle({ progress, remainingCount }: ProgressCircleProps): JSX.Element {
  const { classes } = useStyles();
  // SVG size for progress circle (theme.spacing(3.5) = 28px)
  const size = 28;
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className={classes.progressContainer}>
      <svg height={size} width={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          stroke="#4CAF50"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
          }}
        />
      </svg>
      <div className={classes.progressNumber}>
        <Typography className={classes.circleNumber}>{remainingCount}</Typography>
      </div>
    </div>
  );
}

interface GetStartedNavItemProps {
  selectedNodeId: string;
}

export function GetStartedNavItem({ selectedNodeId }: GetStartedNavItemProps): JSX.Element | null {
  const { classes } = useStyles();
  const { sections } = useOnboarding();
  const { remainingCount, totalCount } = useMemo(() => {
    return sections.quickStart.reduce(
      (acc, step) => {
        acc.totalCount += step.tasks.length;
        acc.remainingCount += step.tasks.filter(t => !t.completedAt).length;
        return acc;
      },
      { remainingCount: 0, totalCount: 0 }
    );
  }, [sections]);

  const selected = selectedNodeId === routes.GetStarted;
  const { classes: navItemClasses } = useLeftNavItemStyles({ selected });
  const progress = totalCount > 0 ? (totalCount - remainingCount) / totalCount : 0;

  // Hide the onboarding button when all steps are completed
  if (remainingCount === 0) return null;

  return (
    <Link className={navItemClasses.content} data-cy="left-nav-get-started" to={routes.GetStarted}>
      <div className={classes.navItems}>
        <ProgressCircle progress={progress} remainingCount={remainingCount} />
        <NavItemText>Onboarding</NavItemText>
      </div>
    </Link>
  );
}
